'use client';

import { Breadcrumb } from '@/components/common/breadcrumb';
import { PageHeader } from '@/components/common/page-header';
import { menuBreadcrumbItems } from '@/config/menu-catalog';
import { ExpandableCard } from '@/components/ui/expandable-card';
import { useToast } from '@/hooks/use-toast';
import { useOrganization } from '@/contexts/organization-context';
import { apiClient } from '@/lib/api-client';
import {
  PetrohqApiResponseListCarWashReportDto,
  PetrohqApiResponseListCarWashDto,
  CarWashReportDto,
  CarWashDto,
} from '@/lib/api/data-contracts';
import { useFilterStore } from '@/stores/filter-store';
import { useCallback, useEffect, useState } from 'react';
import * as RealGrid from 'realgrid';
import { RealGridReact, RGDataColumn, RGDataField } from 'realgrid-react';

export function CarWash() {
  const { toast } = useToast();
  const { getAllStores } = useOrganization();
  // 온디맨드 방식: API 호출 시점에만 필터 상태 읽기

  // 상태 관리
  const [reportData, setReportData] = useState<CarWashReportDto[]>([]);
  const [detailData, setDetailData] = useState<CarWashDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [expandedStates, setExpandedStates] = useState<Record<string, boolean>>(
    {
      report: false,
      detail: false,
    }
  );

  // 세차 데이터 가져오기 함수
  const fetchCarWashData = useCallback(async () => {
    // API 호출 시점에 현재 필터 상태 읽기
    const filters = useFilterStore.getState();

    if (!filters.dateRange?.start || !filters.dateRange?.end) {
      return;
    }

    setIsLoading(true);
    try {
      const storeCodeList =
        filters.store.length > 0 ? filters.store : getAllStores();

      // 세차 일보용 파라미터 (환불 옵션 없음)
      const reportParams = filters.buildApiParams('car-wash', storeCodeList);

      // 세차 상세용 파라미터 (환불 옵션 포함)
      const detailParams = filters.buildApiParams('car-wash', storeCodeList);

      // 병렬 API 호출
      const [reportResponse, detailResponse] = await Promise.all([
        apiClient.get('car-wash/report', { searchParams: reportParams }),
        apiClient.get('car-wash', { searchParams: detailParams }),
      ]);

      // 세차 일보 데이터 처리
      const reportResult =
        (await reportResponse.json()) as PetrohqApiResponseListCarWashReportDto;
      if (reportResult.result) {
        setReportData(reportResult.result);
      }

      // 세차 상세 데이터 처리
      const detailResult =
        (await detailResponse.json()) as PetrohqApiResponseListCarWashDto;
      if (detailResult.result) {
        setDetailData(detailResult.result);
      }
    } catch (error) {
      console.error('Car wash data fetch error:', error);
      toast({
        title: '오류',
        description: '세차 데이터를 가져오는 중 오류가 발생했습니다.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [getAllStores, toast]);

  // 조회 버튼 핸들러
  const handleSearch = () => {
    fetchCarWashData();
  };

  // 초기 데이터 로드
  useEffect(() => {
    fetchCarWashData();
  }, []);

  // 카드 확장 상태 핸들러
  const handleExpandChange = (gridType: string) => (isExpanded: boolean) => {
    setExpandedStates(prev => ({
      ...prev,
      [gridType]: isExpanded,
    }));
  };

  const breadcrumb = <Breadcrumb items={menuBreadcrumbItems('car-wash')} />;

  const pageHeader = (
    <PageHeader onSearch={handleSearch} isLoading={isLoading} />
  );

  return (
    <div className='space-y-6'>
      {/* 세차 일보 그리드 */}
      <ExpandableCard
        title='세차 일보'
        breadcrumb={breadcrumb}
        pageHeader={pageHeader}
        onExpandChange={handleExpandChange('report')}
      >
        <div
          className={`border rounded-lg ${expandedStates.report ? 'h-full' : 'h-[300px]'}`}
        >
          <div className='w-full h-full'>
            <RealGridReact
              gridProps={{
                display: { fitStyle: RealGrid.GridFitStyle.FILL },
                checkBar: { visible: false },
                stateBar: { visible: false },
                edit: { readOnly: true, editable: false },
                groupPanel: { visible: false },
                footer: { visible: true },
                sorting: {
                  enabled: true,
                  showSortOrder: true,
                  style: RealGrid.SortStyle.INCLUSIVE,
                },
              }}
              rows={reportData}
            >
              {/* Data Fields */}
              <RGDataField
                fieldName='compCd'
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName='compName'
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName='storeCd'
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName='storeName'
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName='machineNumber'
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName='washDate'
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName='posSequence'
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName='viewSequence'
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName='washName'
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName='previousDayGauge'
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName='currentDayGauge'
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName='totalWashCount'
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName='totalTestWashCount'
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName='totalServiceWashCount'
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName='totalPaidWashCount'
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName='customerWashCount'
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName='customerWashCost'
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName='customerWashAmount'
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName='paidWashCount'
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName='paidWashCost'
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName='paidWashAmount'
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName='totalWashAmount'
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName='testMemo'
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName='serviceMemo'
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName='remarks'
                dataType={RealGrid.ValueType.TEXT}
              />

              {/* Data Columns */}
              <RGDataColumn
                fieldName='storeName'
                header={{ text: '매장' }}
                mergeRule='value'
              />
              <RGDataColumn
                fieldName='machineNumber'
                header={{ text: '세차기' }}
              />
              <RGDataColumn
                fieldName='washDate'
                header={{ text: '세차일자' }}
                mergeRule='value'
              />
              <RGDataColumn
                fieldName='posSequence'
                header={{ text: 'POS순서' }}
              />
              <RGDataColumn
                fieldName='viewSequence'
                header={{ text: '보기순서' }}
              />
              <RGDataColumn
                fieldName='washName'
                header={{ text: '세차명' }}
                groupFooter={{
                  valueCallback: function (
                    _grid: any,
                    _column: any,
                    _footerIndex: number,
                    group: any
                  ) {
                    return group.groupValue + ' 소계: ';
                  },
                }}
                footer={{ text: '총계:' }}
              />
              <RGDataColumn
                fieldName='previousDayGauge'
                header={{ text: '전일메터' }}
                numberFormat='#,##0'
                styleName='text-right'
                footer={{ expression: 'sum', numberFormat: '#,##0' }}
                groupFooter={{ expression: 'sum', numberFormat: '#,##0' }}
              />
              <RGDataColumn
                fieldName='currentDayGauge'
                header={{ text: '당일메터' }}
                numberFormat='#,##0'
                styleName='text-right'
                footer={{ expression: 'sum', numberFormat: '#,##0' }}
                groupFooter={{ expression: 'sum', numberFormat: '#,##0' }}
              />
              <RGDataColumn
                fieldName='totalWashCount'
                header={{ text: '총 세차 건수' }}
                numberFormat='#,##0'
                styleName='text-right'
                footer={{ expression: 'sum', numberFormat: '#,##0' }}
                groupFooter={{ expression: 'sum', numberFormat: '#,##0' }}
              />
              <RGDataColumn
                fieldName='totalTestWashCount'
                header={{ text: 'Test 건수' }}
                numberFormat='#,##0'
                styleName='text-right'
                footer={{ expression: 'sum', numberFormat: '#,##0' }}
                groupFooter={{ expression: 'sum', numberFormat: '#,##0' }}
              />
              <RGDataColumn
                fieldName='totalServiceWashCount'
                header={{ text: '서비스 건수' }}
                numberFormat='#,##0'
                styleName='text-right'
                footer={{ expression: 'sum', numberFormat: '#,##0' }}
                groupFooter={{ expression: 'sum', numberFormat: '#,##0' }}
              />
              <RGDataColumn
                fieldName='paidWashCount'
                header={{ text: '건수' }}
                numberFormat='#,##0'
                styleName='text-right'
                footer={{ expression: 'sum', numberFormat: '#,##0' }}
                groupFooter={{ expression: 'sum', numberFormat: '#,##0' }}
              />
              <RGDataColumn
                fieldName='paidWashCost'
                header={{ text: '단가' }}
                numberFormat='#,##0'
                styleName='text-right'
              />
              <RGDataColumn
                fieldName='paidWashAmount'
                header={{ text: '금액' }}
                numberFormat='#,##0'
                styleName='text-right'
                footer={{ expression: 'sum', numberFormat: '#,##0' }}
                groupFooter={{ expression: 'sum', numberFormat: '#,##0' }}
              />
              <RGDataColumn
                fieldName='serviceMemo'
                header={{ text: '서비스메모' }}
              />
              <RGDataColumn fieldName='remarks' header={{ text: '비고' }} />
            </RealGridReact>
          </div>
        </div>
      </ExpandableCard>

      {/* 세차 상세 그리드 */}
      <ExpandableCard
        title='세차 상세'
        onExpandChange={handleExpandChange('detail')}
      >
        <div
          className={`border rounded-lg ${expandedStates.detail ? 'h-full' : 'h-[400px]'}`}
        >
          <div className='w-full h-full'>
            <RealGridReact
              gridProps={{
                display: { fitStyle: RealGrid.GridFitStyle.FILL },
                checkBar: { visible: false },
                stateBar: { visible: false },
                edit: { readOnly: true, editable: false },
                groupPanel: { visible: true },
                footer: { visible: true },
                sorting: {
                  enabled: true,
                  showSortOrder: true,
                  style: RealGrid.SortStyle.INCLUSIVE,
                },
              }}
              rows={detailData}
            >
              {/* Data Fields */}
              <RGDataField
                fieldName='compCd'
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName='compName'
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName='storeCd'
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName='storeName'
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName='closeDate'
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName='carWashMachine'
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName='carWashNo'
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName='carNo'
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName='bigo'
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName='payAmount'
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName='carTypeName'
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName='payTypeName'
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName='additionalOption'
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName='discountName'
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName='carWashCourseName'
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName='carWashItemName'
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName='carType'
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName='additionalOption1'
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName='additionalOption2'
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName='additionalOption3'
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName='additionalOption4'
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName='carWashCourse'
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName='carWashItem'
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName='payType'
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName='carWashDate'
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName='retryYn'
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName='refundYn'
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName='errorYn'
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName='originalPosNo'
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName='discountAmount'
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName='cardNo'
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName='customerCode'
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName='carWashType'
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName='totalSeq'
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName='carWashServerNo'
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName='fullCarNo'
                dataType={RealGrid.ValueType.TEXT}
              />

              {/* Data Columns */}
              <RGDataColumn
                fieldName='storeName'
                header={{ text: '매장' }}
                mergeRule='value'
              />
              <RGDataColumn
                fieldName='closeDate'
                header={{ text: '마감일' }}
                mergeRule='value'
                groupFooter={{
                  valueCallback: function (
                    _grid: any,
                    _column: any,
                    _footerIndex: number,
                    group: any
                  ) {
                    return group.groupValue + ' 소계: ';
                  },
                }}
                footer={{ text: '총계:' }}
              />
              <RGDataColumn
                fieldName='carWashMachine'
                header={{ text: '세차기' }}
              />
              <RGDataColumn
                fieldName='carWashNo'
                header={{ text: 'NO' }}
                groupFooter={{ text: '세차건수: ' }}
                footer={{ text: '세차건수: ' }}
              />
              <RGDataColumn
                fieldName='carNo'
                header={{ text: '차량번호' }}
                groupFooter={{ expression: 'count', numberFormat: '#,##0' }}
                footer={{ expression: 'count', numberFormat: '#,##0' }}
              />
              <RGDataColumn fieldName='bigo' header={{ text: '비고' }} />
              <RGDataColumn
                fieldName='payAmount'
                header={{ text: '결제금액' }}
                numberFormat='#,##0'
                styleName='text-right'
                groupFooter={{ expression: 'sum', numberFormat: '#,##0' }}
                footer={{ expression: 'sum', numberFormat: '#,##0' }}
              />
              <RGDataColumn fieldName='carTypeName' header={{ text: '차종' }} />
              <RGDataColumn
                fieldName='payTypeName'
                header={{ text: '결제방법' }}
              />
              <RGDataColumn
                fieldName='additionalOption'
                header={{ text: '추가옵션' }}
              />
              <RGDataColumn
                fieldName='discountName'
                header={{ text: '할인명' }}
              />
              <RGDataColumn
                fieldName='carWashCourseName'
                header={{ text: '세차코스' }}
              />
              <RGDataColumn
                fieldName='carWashItemName'
                header={{ text: '세차품목' }}
              />
              <RGDataColumn fieldName='carType' header={{ text: '차종코드' }} />
              <RGDataColumn
                fieldName='additionalOption1'
                header={{ text: '추가옵션1' }}
              />
              <RGDataColumn
                fieldName='additionalOption2'
                header={{ text: '추가옵션2' }}
              />
              <RGDataColumn
                fieldName='additionalOption3'
                header={{ text: '추가옵션3' }}
              />
              <RGDataColumn
                fieldName='additionalOption4'
                header={{ text: '추가옵션4' }}
              />
              <RGDataColumn
                fieldName='carWashCourse'
                header={{ text: '세차코스코드' }}
              />
              <RGDataColumn
                fieldName='carWashItem'
                header={{ text: '세차품목코드' }}
              />
              <RGDataColumn
                fieldName='payType'
                header={{ text: '결제방법코드' }}
              />
              <RGDataColumn
                fieldName='carWashDate'
                header={{ text: '세차일자' }}
              />
              <RGDataColumn fieldName='retryYn' header={{ text: '재시도' }} />
              <RGDataColumn fieldName='refundYn' header={{ text: '환불' }} />
              <RGDataColumn fieldName='errorYn' header={{ text: '오류' }} />
              <RGDataColumn
                fieldName='originalPosNo'
                header={{ text: '원시도번호' }}
              />
              <RGDataColumn
                fieldName='discountAmount'
                header={{ text: '할인금액' }}
              />
              <RGDataColumn fieldName='cardNo' header={{ text: '카드번호' }} />
              <RGDataColumn
                fieldName='customerCode'
                header={{ text: '고객코드' }}
              />
              <RGDataColumn
                fieldName='carWashType'
                header={{ text: '할인코드' }}
              />
              <RGDataColumn
                fieldName='totalSeq'
                header={{ text: '전체거래순서' }}
              />
              <RGDataColumn
                fieldName='carWashServerNo'
                header={{ text: '세차서버번호' }}
              />
              <RGDataColumn
                fieldName='fullCarNo'
                header={{ text: '전체차량번호' }}
              />
            </RealGridReact>
          </div>
        </div>

        {/* 데이터 카운트 */}
        <div className='mt-4 flex items-center justify-between'>
          <div className='flex items-center space-x-4'>
            <span className='text-sm text-muted-foreground'>
              일보: {reportData.length}건 / 상세: {detailData.length}건
            </span>
            {isLoading && (
              <span className='text-sm text-blue-600'>로딩 중...</span>
            )}
          </div>
        </div>
      </ExpandableCard>
    </div>
  );
}
